import { useMemo } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/modern/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/modern/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/modern/components/ui/popover";
import {
  useCoverage,
  type PromotionDateRange,
  type PromotedAssetSummary,
} from "../context/CoverageContext";
import { kpiContextBadgeClass } from "../constants";

// ─── Chart layout constants ──────────────────────────────────────────────────

const BAR_MAX_H = 36;
const BAR_AREA_H = 54;
const SLOT_W = 56;
const COL_GAP = 24;

const DATE_RANGE_OPTIONS: { value: PromotionDateRange; label: string }[] = [
  { value: "7d", label: "7D" },
  { value: "14d", label: "14D" },
  { value: "1m", label: "1M" },
  { value: "all", label: "All" },
];

const RANGE_SUFFIX: Record<PromotionDateRange, string> = {
  "7d": "in the last 7 days",
  "14d": "in the last 14 days",
  "1m": "in the last month",
  all: "all time",
};

const AVATAR_COLORS = [
  "hsl(0 72% 51%)",
  "hsl(207 77% 51%)",
  "hsl(142 76% 36%)",
  "hsl(45 97% 54%)",
  "hsl(280 67% 51%)",
  "hsl(174 86% 45%)",
  "hsl(25 95% 53%)",
  "hsl(339 82% 51%)",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAvatarColor(userId: number): string {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 1).toUpperCase();
}

function formatRelativeDate(date: Date): string {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AssetList({ assets }: { assets: PromotedAssetSummary[] }) {
  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        <div className="rounded-full bg-muted p-2.5">
          <svg className="size-4 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 9.75h.008v.008H3V9.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
        </div>
        <p className="text-xs text-muted-foreground/60">No assets promoted in this period</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {assets.map((asset, i) => (
        <div
          key={asset.id}
          className={cn(
            "flex gap-3 px-3 py-2.5 transition-colors hover:bg-muted/40",
            i !== assets.length - 1 && "border-b border-border/40",
          )}
        >
          <span className="mt-0.5 shrink-0 text-[10px] font-medium text-muted-foreground/40 tabular-nums w-4 text-right">
            {i + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <p className="text-xs text-foreground leading-snug line-clamp-2 flex-1">
                {asset.caption || <span className="italic text-muted-foreground/40">No caption</span>}
              </p>
              <span className="shrink-0 text-[10px] text-muted-foreground/50 tabular-nums whitespace-nowrap">
                {formatRelativeDate(asset.createdAt)}
              </span>
            </div>
            {asset.problemHubLabels.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {asset.problemHubLabels.map((label) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 rounded-sm bg-primary/10 border border-primary/20 px-1.5 py-px text-[10px] text-primary leading-none font-medium"
                  >
                    <svg className="size-2 shrink-0" viewBox="0 0 8 8" fill="currentColor">
                      <circle cx="4" cy="4" r="3" />
                    </svg>
                    {label}
                  </span>
                ))}
              </div>
            )}
            {asset.tagLabels.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {asset.tagLabels.map((label) => (
                  <span
                    key={label}
                    className="rounded-sm bg-muted px-1.5 py-px text-[10px] text-muted-foreground leading-none border border-border/50"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ChartUser {
  userId: number;
  name: string;
  profileImageURL?: string;
  promotions: number;
  isActive: boolean;
  barH: number;
  promotedAssets: PromotedAssetSummary[];
}

function UserBarChart({
  users,
  avgBarH,
  promotionDateRange,
}: {
  users: ChartUser[];
  avgBarH: number;
  promotionDateRange: PromotionDateRange;
}) {
  return (
    <div className="flex flex-col">
      {/* Bar area — relative for avg line overlay */}
      <div className="relative" style={{ height: BAR_AREA_H }}>
        {avgBarH > 0 && (
          <div
            className="absolute left-0 right-0 flex items-center gap-1 pointer-events-none"
            style={{ bottom: avgBarH }}
          >
            <div className="flex-1 border-t border-dashed border-muted-foreground/30" />
            <span className="shrink-0 text-[9px] text-muted-foreground/40 pr-1">avg</span>
          </div>
        )}
        <div className="flex items-end h-full" style={{ gap: COL_GAP }}>
          {users.map((user) => (
            <div
              key={user.userId}
              className="flex shrink-0 flex-col items-center justify-end gap-1"
              style={{ width: SLOT_W, height: BAR_AREA_H }}
            >
              <span className={cn(
                "text-[11px] font-semibold leading-none tabular-nums",
                user.isActive ? "text-foreground" : "text-muted-foreground/40",
              )}>
                {user.promotions}
              </span>
              <div
                className={cn("w-2.5 rounded-full", user.isActive ? "bg-primary" : "bg-muted-foreground/20")}
                style={{ height: user.barH }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Avatar row */}
      <div className="flex mt-1" style={{ gap: COL_GAP }}>
        {users.map((user) => {
          const lastAsset = user.promotedAssets[0];
          return (
            <Popover key={user.userId}>
              <PopoverTrigger asChild>
                <div
                  className="flex shrink-0 flex-col items-center gap-1 cursor-pointer group"
                  style={{ width: SLOT_W }}
                >
                  <Avatar className={cn(
                    "h-8 w-8 ring-2 ring-transparent transition-all group-hover:ring-primary/40",
                    !user.isActive && "opacity-40",
                  )}>
                    {user.profileImageURL
                      ? <AvatarImage src={user.profileImageURL} alt={user.name} />
                      : null}
                    <AvatarFallback
                      className="text-[10px] font-semibold text-white"
                      style={{ backgroundColor: getAvatarColor(user.userId) }}
                    >
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    title={lastAsset
                      ? `Last promoted: ${lastAsset.createdAt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}`
                      : "No promotions in this period"
                    }
                    className={cn(
                      "text-[9px] leading-none whitespace-nowrap tabular-nums cursor-default transition-colors",
                      user.isActive
                        ? "text-muted-foreground/55 hover:text-foreground"
                        : "text-muted-foreground/25",
                    )}
                  >
                    {lastAsset ? formatRelativeDate(lastAsset.createdAt) : "—"}
                  </span>
                </div>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="center"
                sideOffset={10}
                collisionPadding={16}
                className="w-80 p-0 shadow-2xl border-border/80 bg-card"
              >
                <div className="flex items-center gap-3 px-4 py-3 bg-muted/60 border-b border-border rounded-t-[inherit]">
                  <Avatar className="h-9 w-9 shrink-0 ring-2 ring-border">
                    {user.profileImageURL
                      ? <AvatarImage src={user.profileImageURL} alt={user.name} />
                      : null}
                    <AvatarFallback
                      className="text-[11px] font-semibold text-white"
                      style={{ backgroundColor: getAvatarColor(user.userId) }}
                    >
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate leading-tight">{user.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className={cn("font-medium", user.isActive ? "text-primary" : "text-muted-foreground")}>
                        {user.promotions}
                      </span>
                      {" "}{user.promotions === 1 ? "promotion" : "promotions"} · {RANGE_SUFFIX[promotionDateRange]}
                    </p>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto bg-card rounded-b-[inherit]">
                  <AssetList assets={user.promotedAssets} />
                </div>
              </PopoverContent>
            </Popover>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function KpiValueSkeleton() {
  return <Loader2 className="size-3.5 animate-spin text-muted-foreground/60" aria-hidden />;
}

export const ProductivityChart = () => {
  const {
    promotionsByUser,
    promotionDateRange,
    setPromotionDateRange,
    totalPromotions,
    kpiData,
    summaryLoading,
    summaryFetching,
    activityLoading,
    activityFetching,
  } = useCoverage();

  const chartUsers = useMemo<ChartUser[]>(() => {
    const maxPromotions = Math.max(...promotionsByUser.map((u) => u.promotions), 1);
    return promotionsByUser.map((u) => ({
      userId: u.userId,
      name: u.name,
      profileImageURL: u.profileImageURL,
      promotions: u.promotions,
      isActive: u.promotions > 0,
      barH: u.promotions > 0
        ? Math.max(4, Math.round((u.promotions / maxPromotions) * BAR_MAX_H))
        : 4,
      promotedAssets: u.promotedAssets,
    }));
  }, [promotionsByUser]);

  const avgBarH = useMemo(() => {
    const activeUsers = chartUsers.filter((u) => u.isActive);
    if (activeUsers.length <= 1) return 0;
    const avg = chartUsers.reduce((s, u) => s + u.promotions, 0) / activeUsers.length;
    const maxCount = Math.max(...chartUsers.map((u) => u.promotions), 1);
    return Math.max(4, Math.round((avg / maxCount) * BAR_MAX_H));
  }, [chartUsers]);

  const noData = !summaryLoading && kpiData.hubsTotal === 0;
  const readyPercent = kpiData.hubsTotal > 0
    ? Math.round((kpiData.outputReadyCount / kpiData.hubsTotal) * 100)
    : 0;

  return (
    <div className="flex flex-col w-full overflow-hidden rounded-xl border border-border bg-card">
      {/* Unified info bar */}
      <div
        className={cn(
          "flex items-center border-b border-border transition-opacity",
          summaryFetching && !summaryLoading && "opacity-70"
        )}
      >

        {/* Promotions + date range */}
        <div className="flex items-center gap-3 px-4 py-2.5 shrink-0 flex-1">
          <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5 shrink-0">
            {DATE_RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPromotionDateRange(opt.value)}
                className={cn(
                  "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
                  promotionDateRange === opt.value
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted-foreground/15 hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="text-sm text-foreground inline-flex items-center gap-2">
            {activityLoading ? (
              <KpiValueSkeleton />
            ) : (
              <span className="text-2xl font-semibold tracking-tight">{totalPromotions}</span>
            )}
            {" "}
            <span className="font-medium">{totalPromotions === 1 ? "Promotion" : "Promotions"}</span>
            {" "}
            <span className="text-muted-foreground">{RANGE_SUFFIX[promotionDateRange]}</span>
            {activityFetching && !activityLoading && (
              <Loader2 className="size-3.5 animate-spin text-muted-foreground/50" aria-label="Refreshing activity" />
            )}
          </span>
        </div>

        {/* Output ready */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-l border-border/50">
          <span className="text-xs text-muted-foreground shrink-0">Output ready</span>
          {summaryLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <span className={cn("text-base font-semibold leading-none", noData ? "text-muted-foreground/40" : "text-success")}>
                {noData ? "—" : kpiData.outputReadyCount}
              </span>
              {!noData && <span className="text-xs text-muted-foreground/50">/ {kpiData.hubsTotal}</span>}
              {!noData && (
                <span className={cn("text-xs font-medium rounded-full px-1.5 py-0.5", kpiContextBadgeClass("landscape_ready"))}>
                  {readyPercent}%
                </span>
              )}
            </>
          )}
        </div>

        {/* Roles filled */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-l border-border/50">
          <span className="text-xs text-muted-foreground shrink-0">Roles filled</span>
          {summaryLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <span className={cn("text-base font-semibold leading-none", noData ? "text-muted-foreground/40" : "text-amber-500")}>
                {noData ? "—" : `${kpiData.requiredPercent}%`}
              </span>
              {!noData && <span className="text-xs text-muted-foreground/50">{kpiData.requiredFilled}/{kpiData.requiredTotal}</span>}
            </>
          )}
        </div>

        {/* Sequence capable */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-l border-border/50">
          <span className="text-xs text-muted-foreground shrink-0">Sequence</span>
          {summaryLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <span className={cn("text-base font-semibold leading-none", noData ? "text-muted-foreground/40" : "text-primary")}>
                {noData ? "—" : kpiData.sequenceReadyCount}
              </span>
              {!noData && (
                <span className={cn("text-xs font-medium rounded-full px-1.5 py-0.5", kpiContextBadgeClass("sequence_capable"))}>
                  Before / After
                </span>
              )}
            </>
          )}
        </div>

        {/* Blind spots */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-l border-border/50">
          <span className="text-xs text-muted-foreground shrink-0">Blind spots</span>
          {summaryLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <span className={cn(
                "text-base font-semibold leading-none",
                noData ? "text-muted-foreground/40" : kpiData.blindSpotCount > 0 ? "text-destructive" : "text-success",
              )}>
                {noData ? "—" : kpiData.blindSpotCount}
              </span>
              {!noData && (
                <span className={cn(
                  "text-xs font-medium rounded-full px-1.5 py-0.5",
                  kpiData.blindSpotCount > 0
                    ? kpiContextBadgeClass("blind_spot_attention")
                    : kpiContextBadgeClass("blind_spot_clear"),
                )}>
                  {kpiData.blindSpotCount > 0 ? "Needs attention" : "✓"}
                </span>
              )}
            </>
          )}
        </div>

      </div>

      {/* Chart area */}
      <div className="relative bg-background px-3 py-3">
        {activityLoading ? (
          <div className="flex h-16 items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <p className="text-xs">Loading activity…</p>
          </div>
        ) : chartUsers.length > 0 ? (
          <div
            className={cn(
              "overflow-x-auto select-none transition-opacity",
              activityFetching && "opacity-60"
            )}
          >
            <UserBarChart
              users={chartUsers}
              avgBarH={avgBarH}
              promotionDateRange={promotionDateRange}
            />
          </div>
        ) : (
          <div className="flex h-16 items-center justify-center">
            <p className="text-xs text-muted-foreground/60">No promotion data</p>
          </div>
        )}
        {activityFetching && !activityLoading && chartUsers.length > 0 && (
          <div className="pointer-events-none absolute right-3 top-3">
            <Loader2 className="size-3.5 animate-spin text-muted-foreground/50" aria-label="Refreshing chart" />
          </div>
        )}
      </div>
    </div>
  );
};
